/**
 * FilterChipsDisplayMolecule
 *
 * Generic, reusable component for displaying active filters as removable chips.
 *
 * Features:
 * - Display property filters as chips with readable labels
 * - Display perspective filters as chips
 * - Display filter groups (OR/AND logic)
 * - Individual chip removal
 * - Clear all button
 * - Compact and expanded views
 *
 * Use cases:
 * - TreeMap column filter summary
 * - Atlas filter summary
 * - Any component that needs to show active filters
 */

import React, { useCallback, useMemo } from 'react';
import { Button } from 'primereact/button';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import { useTranslation } from 'localization';
import { ArcFilter, FilterExpression, RelationshipFilter } from 'types/filters';
import { PerspectiveFilterState } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import { renderArcFilter } from '../../../utils/renderFilterExpression';
import { prettifyKey } from '../../../utils/filterPropertyBuilder';
import './FilterChipsDisplayMolecule.scss';

export interface FilterChipsDisplayMoleculeProps {
  /** Property filters to display */
  propertyFilters?: FilterExpression[];

  /** Perspective filters to display */
  perspectiveFilters?: PerspectiveFilterState[];

  /** Relationship filters to display (SC4) */
  relationshipFilters?: RelationshipFilter[];

  /**
   * Arc filters to display (EAINT-793 R-19).
   *
   * Added because callers already COUNTED this lane while this molecule could
   * not render it: `hasAnyFilterLane` includes `arcFilters`, so a layer whose
   * only clause on a level was an arc filter drew a heading and a badge of "1"
   * over an empty body — in the popover AND in the filter bar.
   */
  arcFilters?: ArcFilter[];

  /** Filter groups to display (OR/AND logic) */
  filterGroups?: any[];

  /** Callback when a property filter is removed */
  onRemovePropertyFilter?: (index: number) => void;

  /** Callback when a perspective filter is removed */
  onRemovePerspectiveFilter?: (perspectiveSystemName: string) => void;

  /** Callback when a relationship filter is removed */
  onRemoveRelationshipFilter?: (index: number) => void;

  /** Callback when an arc filter is removed */
  onRemoveArcFilter?: (index: number) => void;

  /** Callback when a filter group is removed */
  onRemoveFilterGroup?: (index: number) => void;

  /** Callback when clear all is clicked */
  onClearAll?: () => void;

  /**
   * Custom property label resolver. `aspect` (the composing aspect's
   * `system_name`, from `FilterExpression.aspect`) is passed alongside the
   * bare `propertyKey` so a resolver can look the option up by `property`
   * first, then by `aspectIdentity` (`<aspect>.<propertyKey>`) — the merged
   * catalog dedupes on the latter, not the former (SC1).
   */
  getPropertyLabel?: (propertyKey: string, aspect?: string) => string;

  /** Custom perspective label resolver */
  getPerspectiveLabel?: (perspectiveSystemName: string) => string;

  /** Custom relationship label resolver */
  getRelationshipLabel?: (relationshipSystemName: string) => string;


  /** Custom property value label resolver (for enum values) */
  getPropertyValueLabel?: (propertyKey: string, value: string) => string;

  /** Maximum chips to show before collapsing (default: 5) */
  maxVisible?: number;

  /** Show "Clear All" button */
  showClearAll?: boolean;

  /** Custom CSS class */
  className?: string;

  /** Compact mode (smaller chips, no descriptions) */
  compact?: boolean;

  /**
   * Shows the built-in "Active Filters (N)" header. Default `true`. Set
   * `false` when a caller (e.g. `FilterProvenanceGroupMolecule`) renders its
   * own heading and composes this molecule for the chip list only (EAINT-781).
   */
  showHeader?: boolean;

  /**
   * Renders every chip locked (non-dismissable, lock affordance) regardless
   * of the `onRemove*` callbacks — the origin-group precedent (EAINT-781):
   * within one provenance group every chip shares one layer, so locking is a
   * per-instance flag, not per-chip. Replaces a per-filter-id provenance map,
   * which this molecule never needs since a caller already scopes it to one
   * layer's clauses.
   */
  locked?: boolean;

  /** Accessible reason forwarded to every `ChipAtom` when `locked`. */
  lockedReason?: string;

  /**
   * Optional provenance badge shown at the trailing end of every chip (e.g.
   * `[CONTEXT]`, `[VIEW]`). Identifies the origin layer at a glance without
   * requiring callers to duplicate the label on each chip individually.
   */
  provenanceBadge?: string;
  /** Colour variant for the provenance badge. */
  provenanceBadgeVariant?: 'context' | 'view' | 'user' | 'session';
  /** Parity instrumentation only — the provenance badge (`uf-column.pill#2`/`.pill#4`). */
  provenanceBadgeDatumId?: string;
}

const FilterChipsDisplayMolecule: React.FC<FilterChipsDisplayMoleculeProps> = ({
  propertyFilters = [],
  perspectiveFilters = [],
  relationshipFilters = [],
  arcFilters = [],
  filterGroups = [],
  onRemovePropertyFilter,
  onRemovePerspectiveFilter,
  onRemoveRelationshipFilter,
  onRemoveArcFilter,
  onRemoveFilterGroup,
  onClearAll,
  getPropertyLabel = (key) => key,
  getPerspectiveLabel = (name) => name,
  getRelationshipLabel = (name) => name,
  getPropertyValueLabel = (key, value) => value,
  maxVisible = 5,
  showClearAll = true,
  className = '',
  compact = false,
  showHeader = true,
  locked = false,
  lockedReason,
  provenanceBadge,
  provenanceBadgeVariant,
  provenanceBadgeDatumId,
}) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = React.useState(false);

  // Format property filter for display
  const formatPropertyFilter = useCallback((filter: FilterExpression): string => {
    const label = getPropertyLabel(filter.property, filter.aspect);
    const operator = filter.operator.replaceAll('_', ' ');

    // Handle filters with 'values' array (e.g., 'in' operator)
    let displayValue: any;
    if (filter.values && Array.isArray(filter.values) && filter.values.length > 0) {
      // Map each value to its label
      displayValue = filter.values
        .map((v: any) => getPropertyValueLabel(filter.property, v))
        .join(', ');
    } else if (filter.value !== undefined && filter.value !== null) {
      displayValue = filter.value;
    }

    // If no value to display, just show property and operator
    if (displayValue === undefined || displayValue === null) {
      return `${label} ${operator}`;
    }

    // Format value for display
    let formattedValue: string | number | boolean = displayValue;
    if (displayValue instanceof Date) {
      formattedValue = displayValue.toLocaleDateString();
    } else if (typeof displayValue === 'string') {
      // Get the proper label for the value
      formattedValue = getPropertyValueLabel(filter.property, displayValue);
    }

    return compact
      ? `${label}: ${formattedValue}`
      : `${label} ${operator} "${formattedValue}"`;
  }, [getPropertyLabel, getPropertyValueLabel, compact]);

  // Format perspective filter for display
  const formatPerspectiveFilter = useCallback((filter: PerspectiveFilterState): string | null => {
    const label = getPerspectiveLabel(filter.perspectiveSystemName);

    if (!filter.includeInHierarchy) {
      return `${t('filters.exclude')} ${label}`;
    }

    if (filter.assigneeFilter?.filterType === 'specific' && filter.assigneeFilter.assigneeIds?.length) {
      const assigneeNames = filter.assigneeFilter.selectedAssignees?.map(a => a.name).join(', ') ||
                           `${filter.assigneeFilter.assigneeIds.length} ${t('filters.users')}`;
      return compact
        ? `${label}: ${assigneeNames}`
        : `${label} (${assigneeNames})`;
    }

    return null; // Don't show if it's just "include with any user" (default state)
  }, [getPerspectiveLabel, compact, t]);

  // Format relationship filter for display (SC4). Never resolves related-sphere
  // names here — the chip shows the relationship + a count, matching the
  // never-persist-names rule (per-sphere names are resolved in the row editor).
  const formatRelationshipFilter = useCallback((filter: RelationshipFilter): string => {
    const label = getRelationshipLabel(filter.relationshipSystemName);
    if (filter.operator === 'not_in') {
      return `${t('filters.exclude')} ${label}`;
    }
    const scope = filter.relatedTo.length === 0
      ? t('filters.anySphere', { defaultValue: 'any sphere' })
      : t('filters.relatedSphereCount', { count: filter.relatedTo.length, defaultValue: `${filter.relatedTo.length} sphere(s)` });
    return compact ? `${label}: ${scope}` : `${label} — ${scope}`;
  }, [getRelationshipLabel, compact, t]);

  // Format arc filter for display. Delegates to the shared `renderArcFilter`
  // rather than re-deriving the axis wording — the same renderer the advanced
  // editor's preview uses, so an arc row reads identically wherever it appears.
  //
  // Health and lifecycle resolve through the SAME keys the arc editor's option
  // lists use (`buildFilterArcOptions`), so a chip never contradicts the editor
  // that authored it and never shows a raw `at_risk` token — which is what it
  // did in every locale before these resolvers were supplied.
  //
  // The row labels itself from its own fields; no label-resolver prop is taken,
  // because no caller of this molecule holds an arc-template catalog today and
  // an unused prop is just API surface to get wrong. `stageId` therefore keeps
  // `renderArcFilter`'s documented raw-id fallback — add a resolver prop when a
  // caller exists that can actually satisfy it.
  const formatArcFilter = useCallback((filter: ArcFilter): string => {
    const selectorKey =
      filter.arcSelector === 'primary'
        ? 'context.editor.arcFilters.selector.primary'
        : 'filters.lane.arc.badge';
    const arcLabel = filter.templateSystemName
      ? prettifyKey(filter.templateSystemName)
      : t(selectorKey);

    return renderArcFilter(filter, {
      arcLabel,
      resolveEnumLabel: (axis, value) => t(`arc.${axis}.${value}`, { defaultValue: prettifyKey(value) }),
      labels: {
        none: t('context.editor.arcFilters.noArcMatches'),
        exists: t('context.editor.arcFilters.existenceCheck'),
        negated: t('context.editor.arcFilters.negatedConnector'),
      },
    });
  }, [t]);

  // Format filter group for display
  const formatFilterGroup = useCallback((group: any): string => {
    const operator = group.operator?.toUpperCase() || 'OR';
    const filterDescriptions = group.filters.map((filter: any) => {
      const label = getPropertyLabel(filter.property, filter.aspect);

      // Get value labels (handle both single value and array of values)
      let displayValues: string;
      if (filter.values && Array.isArray(filter.values)) {
        // Map each value to its label
        displayValues = filter.values
          .map((v: string) => getPropertyValueLabel(filter.property, v))
          .join(', ');
      } else if (filter.value !== undefined && filter.value !== null) {
        displayValues = getPropertyValueLabel(filter.property, filter.value);
      } else {
        displayValues = '';
      }

      return displayValues ? `${label}: ${displayValues}` : label;
    });
    const separator = ' ' + operator + ' ';
    return `(${filterDescriptions.join(separator)})`;
  }, [getPropertyLabel, getPropertyValueLabel]);

  // Count total active filters
  const totalFilters = useMemo(() => {
    let count = propertyFilters.length + filterGroups.length + relationshipFilters.length + arcFilters.length;

    // Only count perspective filters that are non-default
    count += perspectiveFilters.filter(f =>
      !f.includeInHierarchy ||
      (f.assigneeFilter?.filterType === 'specific' && (f.assigneeFilter.assigneeIds?.length || 0) > 0)
    ).length;

    return count;
  }, [propertyFilters, perspectiveFilters, relationshipFilters, arcFilters, filterGroups]);

  // Get visible chips
  const visibleCount = showAll ? totalFilters : Math.min(maxVisible, totalFilters);
  const hasMore = totalFilters > maxVisible;

  if (totalFilters === 0) {
    return null; // Don't render if no filters
  }

  let chipIndex = 0;

  return (
    <div className={`filter-chips-display ${compact ? 'filter-chips-display--compact' : ''} ${className}`}>
      {showHeader && (
        <div className="filter-chips-display__header">
          <div className="filter-chips-display__title">
            <i className="pi pi-filter" />
            <span>{t('filters.activeFilters')} ({totalFilters})</span>
          </div>
          {showClearAll && onClearAll && (
            <Button
              type="button"
              label={t('filters.clearAll')}
              icon="pi pi-times"
              className="p-button-text p-button-sm filter-chips-display__clear-all"
              onClick={onClearAll}
            />
          )}
        </div>
      )}

      <div className="filter-chips-display__chips">
        {/* Property filter chips */}
        {propertyFilters.map((filter, index) => {
          if (chipIndex >= visibleCount) return null;
          chipIndex++;

          return (
            <span
              key={`property-${filter.property}-${filter.operator}-${index}`}
              className="filter-chips-display__chip-wrapper"
            >
            <ChipAtom
              label={formatPropertyFilter(filter)}
              removable={!locked && !!onRemovePropertyFilter}
              onRemove={locked ? undefined : () => { onRemovePropertyFilter?.(index); return false; }}
              className="filter-chips-display__chip filter-chips-display__chip--property"
              icon="pi pi-tag"
              locked={locked}
              lockedReason={lockedReason}
            />
            {provenanceBadge && (
              <span
                className={`filter-chips-display__provenance-badge filter-chips-display__provenance-badge--${provenanceBadgeVariant ?? 'context'}`}
                data-datum={provenanceBadgeDatumId}
                data-kind={provenanceBadgeDatumId ? 'pill' : undefined}
              >
                {provenanceBadge}
              </span>
            )}
            </span>
          );
        })}

        {/* Perspective filter chips */}
        {perspectiveFilters.map((filter) => {
          if (chipIndex >= visibleCount) return null;

          const formatted = formatPerspectiveFilter(filter);
          if (!formatted) return null; // Skip default states

          chipIndex++;

          return (
            <ChipAtom
              key={`perspective-${filter.perspectiveSystemName}`}
              label={formatted}
              removable={!locked && !!onRemovePerspectiveFilter}
              onRemove={locked ? undefined : () => { onRemovePerspectiveFilter?.(filter.perspectiveSystemName); return false; }}
              className="filter-chips-display__chip filter-chips-display__chip--perspective"
              icon="pi pi-users"
              locked={locked}
              lockedReason={lockedReason}
            />
          );
        })}

        {/* Relationship filter chips (SC4) */}
        {relationshipFilters.map((filter, index) => {
          if (chipIndex >= visibleCount) return null;
          chipIndex++;

          return (
            <ChipAtom
              key={`relationship-${filter.relationshipSystemName}-${index}`}
              label={formatRelationshipFilter(filter)}
              removable={!locked && !!onRemoveRelationshipFilter}
              onRemove={locked ? undefined : () => { onRemoveRelationshipFilter?.(index); return false; }}
              className="filter-chips-display__chip filter-chips-display__chip--relationship"
              icon="pi pi-share-alt"
              locked={locked}
              lockedReason={lockedReason}
            />
          );
        })}

        {/* Arc filter chips (EAINT-793 R-19) */}
        {arcFilters.map((filter, index) => {
          if (chipIndex >= visibleCount) return null;
          chipIndex++;

          return (
            <ChipAtom
              key={`arc-${filter.arcSelector ?? 'any'}-${index}`}
              label={formatArcFilter(filter)}
              removable={!locked && !!onRemoveArcFilter}
              onRemove={locked ? undefined : () => { onRemoveArcFilter?.(index); return false; }}
              className="filter-chips-display__chip filter-chips-display__chip--arc"
              icon="pi pi-sitemap"
              locked={locked}
              lockedReason={lockedReason}
            />
          );
        })}

        {/* Filter group chips */}
        {filterGroups.map((group, index) => {
          if (chipIndex >= visibleCount) return null;
          chipIndex++;

          return (
            <ChipAtom
              key={`group-${group.operator}-${index}`}
              label={formatFilterGroup(group)}
              removable={!locked && !!onRemoveFilterGroup}
              onRemove={locked ? undefined : () => { onRemoveFilterGroup?.(index); return false; }}
              className="filter-chips-display__chip filter-chips-display__chip--group"
              icon="pi pi-filter"
              locked={locked}
              lockedReason={lockedReason}
            />
          );
        })}

        {/* Show more/less button */}
        {hasMore && (
          <Button
            type="button"
            label={showAll ? t('filters.showLess') : `+${totalFilters - maxVisible} ${t('filters.more')}`}
            className="p-button-text p-button-sm filter-chips-display__show-more"
            onClick={() => setShowAll(!showAll)}
          />
        )}
      </div>
    </div>
  );
};

export default FilterChipsDisplayMolecule;
